import {sendAnalyticsEvent} from '#/analytics/analytics';
import {ContentTile, ContentTileTitle} from '#/atoms/content-tile';
import {PortfolioAccountContext} from '#/components/portfolio/context/portfolio-account-context';
import {UserAvatarImage} from '#/components/user/user-avatar-image';
import {ModalContext} from '#/contexts/modal-provider';
import {useWalletProvider} from '#/contexts/wallet-provider';
import {useUserAvatar} from '#/hooks/use-user-avatar';
import {useUserDisplayName} from '#/hooks/use-user-display-name';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {PencilSquareIcon} from '@heroicons/react/24/solid';
import {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

export const PortfolioProfile: FunctionComponent<ClassName> = ({className}) => {
  const {account, isCurrentUser} = useContext(PortfolioAccountContext);
  const {open} = useContext(ModalContext);
  const {
    accountResult: {address},
  } = useWalletProvider();
  const {t} = useTranslation('translation');

  const {data: userAvatar} = useUserAvatar({account});
  const {data: userDisplayName} = useUserDisplayName({account: address});

  const onEditProfile = () => {
    open({name: 'UserProfileEditModal', props: {}});
    sendAnalyticsEvent.editUserProfile();
  };

  return (
    <ContentTile className={className}>
      <div className='flex justify-between'>
        <ContentTileTitle>{t('pages.portfolio.sections.info.sections.userProfile.title')}</ContentTileTitle>
        {isCurrentUser && (
          <button className='flex gap-1 text-sm items-center text-gray-400 hover:opacity-70' onClick={onEditProfile}>
            {t('pages.portfolio.sections.info.sections.userProfile.editProfileCta')}
            <PencilSquareIcon className='stroke-gray-400 fill-gray-400 aspect-square w-4 mb-0.5' />
          </button>
        )}
      </div>

      <div className=' gap-4 mt-3 flex items-start'>
        <div className='w-fit shrink-0 relative mb-4'>
          <UserAvatarImage
            avatarUrl={userAvatar}
            displayName={userDisplayName}
            widthClassName={'w-16 @4xl/portfolio:w-24'}
          />
          {isCurrentUser && <CurrentUserIndicator />}
        </div>

        <div className='grow space-y-2 overflow-hidden'>
          <div className=''>
            <div className='flex justify-between ml-1 mb-0'>
              <label className='w-full block text-gray-300 text-xs italic font-semibold'>
                {t('pages.editUserProfile.fields.displayName.label')}
              </label>
            </div>
            <div className='block w-full text-sm outline-none px-1 rounded text-white font-semibold focus:ring-1 focus:ring-deepblue'>
              {userDisplayName}
            </div>
          </div>
        </div>
      </div>
    </ContentTile>
  );
};

export const CurrentUserIndicator: FunctionComponent<DefaultChildren> = () => {
  const {t} = useTranslation('translation');

  return (
    <div className='absolute top-0 w-full h-full flex-center pointer-events-none	'>
      <span className='relative top-1/2 bg-upgreen mx-auto text-white text-xs p-1.5 rounded'>
        {t('components.userProfile.signedInUserIndicator')}
      </span>
    </div>
  );
};
